import { generateClient } from 'aws-amplify/api'
import { env } from '../config/env'
import { formatUnknownError } from '../utils/format-error'
import type { CallHistoryRecord } from '../types/voice-call'

const client = generateClient()

const listCallHistoryQuery = /* GraphQL */ `
  query ListCallHistory($limit: Int) {
    listCallHistory(limit: $limit) {
      callId
      callerId
      calleeId
      peerId
      startedAt
      endedAt
      status
      direction
      mediaType
      isGroup
      transcriptStatus
      summaryPreview
    }
  }
`

async function fetchCallHistory(limit: number): Promise<CallHistoryRecord[]> {
  const result = await client.graphql({
    query: listCallHistoryQuery,
    variables: { limit },
    authMode: 'userPool',
  })
  if ('errors' in result && result.errors?.length) {
    throw new Error(result.errors.map((e) => formatUnknownError(e)).join('; '))
  }
  const items = (result as { data?: { listCallHistory?: CallHistoryRecord[] } }).data
    ?.listCallHistory
  return items ?? []
}

/**
 * Short-TTL stale-while-revalidate cache for call history.
 *
 * The calls page refetches history on every mount, so navigating back to it
 * always showed a "Loading call history…" flash and a fresh network round-trip.
 * We cache the most recent successful result per `limit` and serve it instantly
 * on revisit, refreshing in the background once it ages past the TTL. Concurrent
 * fetches for the same limit are de-duped.
 *
 * Call `invalidateCallHistoryCache()` after a call ends so the next read shows
 * the freshly-recorded call (the calls page already refetches after a call, and
 * the voice-call controller can invalidate on hang-up — see report note).
 */
const CALL_HISTORY_TTL_MS = 30_000

type CachedCallHistory = { records: CallHistoryRecord[]; fetchedAt: number }

const callHistoryCache = new Map<number, CachedCallHistory>()
const callHistoryInFlight = new Map<number, Promise<CallHistoryRecord[]>>()

export function invalidateCallHistoryCache(): void {
  callHistoryCache.clear()
}

function refreshCallHistory(limit: number): Promise<CallHistoryRecord[]> {
  const existing = callHistoryInFlight.get(limit)
  if (existing) return existing
  const request = fetchCallHistory(limit)
    .then((records) => {
      callHistoryCache.set(limit, { records, fetchedAt: Date.now() })
      return records
    })
    .finally(() => {
      callHistoryInFlight.delete(limit)
    })
  callHistoryInFlight.set(limit, request)
  return request
}

export async function listCallHistory(
  limit = 50,
  opts: { throwOnError?: boolean } = {},
): Promise<CallHistoryRecord[]> {
  if (!env.appsyncGraphqlUrl) return []

  const cached = callHistoryCache.get(limit)
  if (cached) {
    if (Date.now() - cached.fetchedAt >= CALL_HISTORY_TTL_MS) {
      // Serve stale immediately, refresh in the background for the next read.
      void refreshCallHistory(limit).catch(() => {})
    }
    return cached.records
  }

  try {
    return await refreshCallHistory(limit)
  } catch (err) {
    // Background readers (notifications feed, car recents) keep the quiet
    // empty fallback; the Calls screen opts in to see the failure so it can
    // keep its saved rows and offer a retry.
    if (opts.throwOnError) throw err
    return []
  }
}
